import React from "react";
import {
  TIERRA, AIRE, cronometroVacio, arrancar, alternar, apagar, resumen,
} from "../../domain/cronometro.js";
import { PALETA } from "../../styles/paleta.js";

/**
 * Cronómetro de vuelo.
 *
 * Un solo botón grande según el momento: arrancar, despegar, aterrizar,
 * apagar. En cabina no hay tiempo de buscar cuál de cinco botones toca, y con
 * guantes tampoco hay puntería. El botón mide 96 px de alto, muy por encima de
 * los 44 px que recomiendan iOS y Android como objetivo táctil mínimo.
 *
 * El reloj se refresca cada segundo, pero el TIEMPO NO SE ACUMULA en ese
 * temporizador: se recalcula desde los instantes guardados. Si el navegador
 * suspende la pestaña —iOS lo hace al apagar la pantalla— el contador visible
 * se congela, pero al volver muestra el tiempo real transcurrido.
 */
export default function Cronometro({ estado, onCambio, onGuardarVuelo }) {
  // Datos que el cronómetro NO puede medir: los carga el piloto al cerrar.
  const [reporte, setReporte] = React.useState({ aterrizajes: null, ngCiclos: "", nfCiclos: "", observaciones: "" });
  const [ahora, setAhora] = React.useState(() => Date.now());
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const id = setInterval(() => setAhora(Date.now()), 1000);
    // Al volver del segundo plano el intervalo puede haber estado detenido:
    // se fuerza un recálculo inmediato en vez de esperar al siguiente tic.
    const alVolver = () => setAhora(Date.now());
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("focus", alVolver);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("focus", alVolver);
    };
  }, []);

  const r = resumen(estado, ahora);

  const aplicar = (fn) => {
    const res = fn(estado, Date.now());
    setError(res.error);
    if (!res.error) onCambio(res.estado);
  };

  const siguiente = (() => {
    if (r.cerrado) return null;
    if (!r.enCurso) return { texto: "Arrancar motor", sub: "empieza el tiempo en tierra", accion: () => aplicar(arrancar), color: PALETA.ok };
    if (r.tipoActual === TIERRA) return { texto: "Despegue", sub: "termina tierra, empieza aire", accion: () => aplicar(alternar), color: PALETA.cyan };
    return { texto: "Aterrizaje", sub: "termina aire, vuelve a tierra", accion: () => aplicar(alternar), color: PALETA.amber };
  })();

  // Dos cifras explícitas: en español toLocaleTimeString devuelve "9:49:52"
  // sin cero delante, y cortar cinco caracteres dejaba "9:49:".
  const hhmm = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("es", { hour12: false, hour: "2-digit", minute: "2-digit" });
  };
  const hms = (ms) => new Date(ms).toLocaleTimeString("es", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const reloj = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  const caja = {
    background: PALETA.panelAlt, border: `1px solid ${PALETA.hairline}`,
    borderRadius: 10, padding: "12px 14px", flex: "1 1 150px", minWidth: 140,
  };
  const rotulo = { fontSize: 10.5, color: PALETA.muted, textTransform: "uppercase", letterSpacing: 1 };
  const cifra = { fontFamily: "'JetBrains Mono', monospace", fontSize: 26, lineHeight: 1.2 };
  const campo = {
    background: PALETA.panel, border: `1px solid ${PALETA.hairline}`, borderRadius: 6,
    color: PALETA.text, padding: "8px 10px", fontFamily: "'JetBrains Mono', monospace",
    fontSize: 16, width: "100%", minHeight: 44,
  };

  return (
    <div>
      {/* Estado actual, grande y legible de un vistazo */}
      <div style={{
        background: r.enCurso ? `${r.tipoActual === AIRE ? PALETA.cyan : PALETA.ok}18` : PALETA.panelAlt,
        border: `2px solid ${r.enCurso ? (r.tipoActual === AIRE ? PALETA.cyan : PALETA.ok) : PALETA.hairline}`,
        borderRadius: 12, padding: "14px 18px", marginBottom: 14, textAlign: "center",
      }}>
        <div style={rotulo}>
          {r.cerrado ? "Vuelo cerrado" : r.enCurso ? (r.tipoActual === AIRE ? "En el aire" : "En tierra, motor en marcha") : "Motor apagado"}
        </div>
        <div style={{ ...cifra, fontSize: 44, color: r.enCurso ? PALETA.text : PALETA.muted }}>
          {reloj(r.ftMs)}
        </div>
        <div style={{ fontSize: 11, color: PALETA.muted }}>desde el arranque</div>
      </div>

      {/* Un solo botón grande: el que corresponde a este momento */}
      <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
        {siguiente && (
          <button
            type="button"
            onClick={siguiente.accion}
            style={{
              background: siguiente.color, border: "none", borderRadius: 12,
              color: "#0A1014", padding: "22px 18px", cursor: "pointer",
              fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
              fontSize: 26, letterSpacing: 1, minHeight: 96, touchAction: "manipulation",
            }}
          >
            {siguiente.texto}
            <div style={{ fontSize: 12, fontWeight: 400, letterSpacing: 0, opacity: 0.75 }}>{siguiente.sub}</div>
          </button>
        )}

        {r.enCurso && (
          <button
            type="button"
            onClick={() => aplicar(apagar)}
            style={{
              background: "transparent", border: `2px solid ${PALETA.danger}`, borderRadius: 12,
              color: PALETA.danger, padding: "16px", cursor: "pointer",
              fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 20,
              letterSpacing: 1, minHeight: 60, touchAction: "manipulation",
            }}
          >
            Apagar motor · cerrar vuelo
          </button>
        )}
      </div>

      {error && <p style={{ fontSize: 12, color: PALETA.danger, margin: "0 0 10px" }}>{error}</p>}
      {r.avisos.map((a, i) => (
        <p key={i} style={{ fontSize: 12, color: PALETA.caution, margin: "0 0 8px" }}>⚠ {a}</p>
      ))}

      {/* Los tres números, con su definición al lado */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={caja}>
          <div style={rotulo}>FT · flight time</div>
          <div style={{ ...cifra, color: PALETA.text }}>{r.ftHhMm ?? "—"}</div>
          <div style={{ fontSize: 10.5, color: PALETA.muted }}>
            {r.ft !== null ? `${r.ft.toFixed(2)} h` : ""} · arranque a apagado
          </div>
        </div>
        <div style={caja}>
          <div style={rotulo}>AT · air time</div>
          <div style={{ ...cifra, color: PALETA.cyan }}>{r.atHhMm ?? "—"}</div>
          <div style={{ fontSize: 10.5, color: PALETA.muted }}>
            {r.at !== null ? `${r.at.toFixed(2)} h` : ""} · solo en el aire
          </div>
        </div>
        <div style={caja}>
          <div style={rotulo}>En tierra</div>
          <div style={{ ...cifra, color: PALETA.muted }}>{r.tierraHhMm ?? "—"}</div>
          <div style={{ fontSize: 10.5, color: PALETA.muted }}>motor en marcha, sin volar</div>
        </div>
        <div style={caja}>
          <div style={rotulo}>Aterrizajes</div>
          <div style={{ ...cifra, color: PALETA.text }}>{r.aterrizajes}</div>
          <div style={{ fontSize: 10.5, color: PALETA.muted }}>tramos de aire cerrados</div>
        </div>
      </div>

      <p style={{ fontSize: 10.5, color: PALETA.muted, lineHeight: 1.55, margin: "0 0 12px" }}>
        <strong>FT — tiempo total</strong>: del arranque al apagado, lo mismo que marca un Hobbs, que corre
        con el motor en marcha esté la aeronave en el aire o esperando en tierra.{" "}
        <strong>AT — air time</strong>: solo los tramos sin contacto con la superficie, o sea FT menos las
        esperas. En un vuelo con muchas tomas y despegues cortos la diferencia es grande, y son justamente
        esas esperas las que no deben contarse como tiempo de vuelo.
      </p>

      {r.tramos.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
            <thead><tr style={{ color: PALETA.muted, textAlign: "left" }}>
              <th>#</th><th>Tramo</th><th>Desde</th><th>Hasta</th><th>Duración</th>
            </tr></thead>
            <tbody>{r.tramos.map((t, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${PALETA.hairline}` }}>
                <td style={{ padding: "4px 0" }}>{i + 1}</td>
                <td style={{ color: t.tipo === AIRE ? PALETA.cyan : PALETA.muted }}>
                  {t.tipo === AIRE ? "aire" : "tierra"}
                </td>
                <td>{hms(t.desdeMs)}</td>
                <td>{t.hastaMs ? hms(t.hastaMs) : "en curso"}</td>
                <td>{reloj(t.duracionMs)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {r.cerrado && (
        <div style={{ marginTop: 16, borderTop: `2px solid ${PALETA.hairline}`, paddingTop: 14 }}>
          <div style={{ ...rotulo, marginBottom: 10 }}>Reporte del vuelo</div>

          <div style={{ fontSize: 11.5, color: PALETA.muted, marginBottom: 10, lineHeight: 1.7 }}>
            Arranque <strong style={{ color: PALETA.text }}>{hhmm(r.horaArranque)}</strong>
            {" · "}Apagado <strong style={{ color: PALETA.text }}>{hhmm(r.horaApagado)}</strong>
            {r.horasAterrizaje.length > 0 && (
              <> · Aterrizajes <strong style={{ color: PALETA.text }}>{r.horasAterrizaje.map(hhmm).join(" · ")}</strong></>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={caja}>
              <div style={rotulo}>Aterrizajes</div>
              <input
                type="number" inputMode="numeric" min={0}
                value={reporte.aterrizajes ?? r.aterrizajes}
                onChange={(e) => setReporte((x) => ({ ...x, aterrizajes: e.target.value === "" ? null : Number(e.target.value) }))}
                style={campo}
              />
              <div style={{ fontSize: 10, color: PALETA.muted }}>contados: {r.aterrizajes} · corregible</div>
            </div>
            <div style={caja}>
              <div style={rotulo}>Ciclos NG</div>
              <input
                type="number" inputMode="numeric" min={0}
                value={reporte.ngCiclos}
                onChange={(e) => setReporte((x) => ({ ...x, ngCiclos: e.target.value }))}
                style={{ ...campo, borderColor: reporte.ngCiclos === "" ? PALETA.caution : PALETA.hairline }}
              />
              <div style={{ fontSize: 10, color: PALETA.muted }}>del indicador o del manual</div>
            </div>
            <div style={caja}>
              <div style={rotulo}>Ciclos NF</div>
              <input
                type="number" inputMode="numeric" min={0}
                value={reporte.nfCiclos}
                onChange={(e) => setReporte((x) => ({ ...x, nfCiclos: e.target.value }))}
                style={{ ...campo, borderColor: reporte.nfCiclos === "" ? PALETA.caution : PALETA.hairline }}
              />
              <div style={{ fontSize: 10, color: PALETA.muted }}>del indicador o del manual</div>
            </div>
            <div style={caja}>
              <div style={rotulo}>Arranques</div>
              <div style={{ ...cifra, color: PALETA.text }}>1</div>
              <div style={{ fontSize: 10, color: PALETA.muted }}>uno por vuelo, por definición</div>
            </div>
          </div>

          <input
            placeholder="Observaciones del vuelo"
            value={reporte.observaciones}
            onChange={(e) => setReporte((x) => ({ ...x, observaciones: e.target.value }))}
            style={{ ...campo, width: "100%", marginBottom: 10 }}
          />

          <p style={{ fontSize: 10.5, color: PALETA.muted, lineHeight: 1.55, margin: "0 0 12px" }}>
            Los ciclos NG y NF <strong>no se pueden deducir del reloj</strong>: dependen del régimen del motor y
            se leen del indicador o se cuentan según el manual. La app no los rellena con una regla supuesta
            —«un arranque, un ciclo» es frecuente pero no universal— porque un ciclo mal contado desfasa el
            remanente de los componentes de vida límite, y ese error no se nota hasta la inspección.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => {
                onGuardarVuelo?.({
                  ftMs: r.ftMs, atMs: r.atMs, tierraMs: r.tierraMs,
                  // Las horas de reloj de cada hito, para la planilla.
                  horaArranque: r.horaArranque,
                  horaApagado: r.horaApagado,
                  horasDespegue: r.horasDespegue,
                  horasAterrizaje: r.horasAterrizaje,
                  aterrizajes: reporte.aterrizajes ?? r.aterrizajes,
                  ngCiclos: reporte.ngCiclos === "" ? null : Number(reporte.ngCiclos),
                  nfCiclos: reporte.nfCiclos === "" ? null : Number(reporte.nfCiclos),
                  observaciones: reporte.observaciones,
                });
                setReporte({ aterrizajes: null, ngCiclos: "", nfCiclos: "", observaciones: "" });
                onCambio(cronometroVacio());
                setError(null);
              }}
              style={{
                background: PALETA.amber, border: "none", borderRadius: 8, color: "#1A1204",
                padding: "14px 18px", cursor: "pointer", fontWeight: 600, minHeight: 52,
              }}
            >
              Guardar vuelo en el día ({r.ftHhMm})
            </button>
            <button
              type="button"
              onClick={() => { onCambio(cronometroVacio()); setError(null); }}
              style={{
                background: "transparent", border: `1px solid ${PALETA.hairline}`, borderRadius: 8,
                color: PALETA.muted, padding: "14px 18px", cursor: "pointer", minHeight: 52,
              }}
            >
              Descartar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
